// UserViewDetails.js
import React, { useEffect, useState } from 'react';
import { Tab, Tabs, Form, Button, Table, Badge } from 'react-bootstrap';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import AttachmentsTab from './AttachmentsTab';
import { isAuthenticated, getUser, updateUser } from '../utils/auth';
import { COUNTRIES } from '../utils/countries';
import PaginationControls from './PaginationControls';

const MANAGING_ROLES = ['admin', 'masteradmin', 'companyadmin', 'departmentadmin'];
const PAGE_SIZE = 20;

// Computed client-side from expiry_date, same held/expired rule as
// get_user_qualification_status() on the backend — no "missing" state here
// since every row in this list is, by definition, held (that's what makes
// it a row at all).
function qualificationExpiryBadge(expiryDate) {
  const isCurrentlyValid = !expiryDate || expiryDate >= new Date().toISOString().slice(0, 10);
  return isCurrentlyValid
    ? <Badge bg="success">Held</Badge>
    : <Badge bg="warning" text="dark">Expired</Badge>;
}

function EmployeeDetails() {
  const { id: employeeId } = useParams();  // Use useParams to get the route parameter
  const [employee, setEmployee] = useState({
    name: '',
    email: '',
    ic_no: '',
    role: '',
    company: '',
    department: '',
    designation: '',
    employee_no: '',
    contact_no: '',
    emergency_contact_no: '',
    relationship: '',
    nationality: '',
    attendedCourses: [],
    qualifications: []
  });
  const [currentUserId, setCurrentUserId] = useState(null); // Add current user ID state
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('profile');
  const [attendedCoursesPage, setAttendedCoursesPage] = useState(1);
  const [keyInstructorPage, setKeyInstructorPage] = useState(1);
  const [qualificationRefreshKey, setQualificationRefreshKey] = useState(0);
  const [allQualifications, setAllQualifications] = useState([]);
  const [newQualification, setNewQualification] = useState({
    qualificationId: '',
    certificateNo: '',
    awardedDate: '',
    expiryDate: ''
  });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [photoUploading, setPhotoUploading] = useState(false);

  const loggedInUser = getUser();
  const navigate = useNavigate();
  console.log ("loggedinuser ", loggedInUser);

  //const employeeId = match.params.id;

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      try {

        // Fetch current user ID from session
        /*
        const sessionRes = await axios.get('${API_BASE_URL}/auth/session.php', {
          withCredentials: true
        });
        
        if (sessionRes.data.user_id) {
          setCurrentUserId(sessionRes.data.user_id);
        }
        */

        const response = await axios.get(`${API_BASE_URL}/users/user_details.php?id=${employeeId}&viewOnly=1`,{
          withCredentials: true
        });
        console.log(response.data); 
        setEmployee(response.data);
      } catch (error) {
        console.error('There was an error fetching the employee details!', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeeDetails();
  }, [employeeId, qualificationRefreshKey]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/qualifications/get_qualifications.php`, { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data)) {
          setAllQualifications(data);
        }
      })
      .catch(error => console.error('Error fetching qualifications:', error));
  }, []);

  const canManage = MANAGING_ROLES.includes(loggedInUser.role) && (
    loggedInUser.role === 'companyadmin' ? employee.company_id === loggedInUser.company_id :
    loggedInUser.role === 'departmentadmin' ? employee.department_id === loggedInUser.department_id :
    true
  );

  // Role-agnostic — viewing your own record, same identity check as the
  // "My Trainings" nav item. Editability of these 4 fields is purely
  // identity-based, not tied to any admin role.
  const isOwnProfile = String(loggedInUser.id) === String(employeeId);

  // A not-yet-decided or rejected registration (always role='employee' —
  // see register.php) gets a restricted view of this same page: no tabs
  // beyond Profile, a status banner, and (rejected only) read-only fields.
  // Only reachable by the account itself — canManage below is always false
  // here, since admin roles never hold a pending/rejected status.
  const isRestrictedOwnProfile = isOwnProfile && employee.status && employee.status !== 'active';
  // Rejected is read-only; pending is fully editable, same as active.
  const canEditOwnProfile = isOwnProfile && employee.status !== 'rejected';

  const handleOwnFieldChange = (e) => {
    const { name, value } = e.target;
    setEmployee(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveOwnProfile = () => {
    const formData = new FormData();
    formData.append('email', employee.email);
    formData.append('contactNo', employee.contact_no);
    formData.append('emergencyContactNo', employee.emergency_contact_no);
    formData.append('relationship', employee.relationship);
    formData.append('nationality', employee.nationality);

    fetch(`${API_BASE_URL}/users/update_own_profile.php`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          Swal.fire({ icon: 'success', text: 'Profile updated.' });
        } else {
          Swal.fire({ icon: 'error', text: data.message || 'Failed to update profile.' });
        }
      })
      .catch(() => Swal.fire({ icon: 'error', text: 'An error occurred while updating your profile.' }));
  };

  const handleNewQualificationChange = (e) => {
    const { name, value } = e.target;
    setNewQualification(prev => ({ ...prev, [name]: value }));
  };

  const handleAddQualification = () => {
    if (!newQualification.qualificationId) {
      Swal.fire({ icon: 'warning', text: 'Please select a qualification/role.' });
      return;
    }
    const formData = new FormData();
    formData.append('userId', employeeId);
    formData.append('qualificationId', newQualification.qualificationId);
    formData.append('certificateNo', newQualification.certificateNo);
    formData.append('awardedDate', newQualification.awardedDate);
    formData.append('expiryDate', newQualification.expiryDate);

    fetch(`${API_BASE_URL}/qualifications/save_user_qualification.php`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setNewQualification({ qualificationId: '', certificateNo: '', awardedDate: '', expiryDate: '' });
          setQualificationRefreshKey(prev => prev + 1);
        } else {
          Swal.fire({ icon: 'error', text: data.message || 'Failed to add qualification/role.' });
        }
      })
      .catch(() => Swal.fire({ icon: 'error', text: 'An error occurred while adding the qualification/role.' }));
  };

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  // isOwnProfile below requires the current password (self-service change);
  // canManage viewing someone else's profile is an admin override — no
  // current password needed, matches change_password.php's own dual-mode
  // authorization (self password_verify vs can_access_employee_record()).
  const handleChangePassword = (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      Swal.fire({ icon: 'warning', text: 'New password and confirmation do not match.' });
      return;
    }
    const formData = new FormData();
    formData.append('userId', employeeId);
    formData.append('currentPassword', passwordForm.currentPassword);
    formData.append('newPassword', passwordForm.newPassword);
    formData.append('confirmPassword', passwordForm.confirmPassword);

    fetch(`${API_BASE_URL}/users/change_password.php`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
          Swal.fire({ icon: 'success', text: data.message });
        } else {
          Swal.fire({ icon: 'error', text: data.message || 'Failed to change password.' });
        }
      })
      .catch(() => Swal.fire({ icon: 'error', text: 'An error occurred while changing the password.' }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('avatar', file);
    if (!isOwnProfile) {
      formData.append('user_id', employeeId);
    }

    setPhotoUploading(true);
    fetch(`${API_BASE_URL}/users/upload_avatar.php`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setEmployee(prev => ({ ...prev, avatar: data.avatar }));
          if (isOwnProfile) {
            updateUser({ avatar: data.avatar });
          }
          Swal.fire({ icon: 'success', text: 'Photo updated.' });
        } else {
          Swal.fire({ icon: 'error', text: data.message || 'Failed to update photo.' });
        }
      })
      .catch(() => Swal.fire({ icon: 'error', text: 'An error occurred while uploading the photo.' }))
      .finally(() => setPhotoUploading(false));
    e.target.value = '';
  };

  const handleApprove = () => {
    Swal.fire({
      title: 'Approve this employee?',
      text: 'This will activate their account.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, approve',
    }).then((result) => {
      if (!result.isConfirmed) return;
      fetch(`${API_BASE_URL}/users/update_registration_status.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: employeeId, newStatus: 'active' }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            Swal.fire('Approved', 'The employee has been approved.', 'success').then(() => navigate('/users'));
          } else {
            Swal.fire('Error', data.message || 'Failed to approve.', 'error');
          }
        })
        .catch(() => Swal.fire('Error', 'An error occurred while approving.', 'error'));
    });
  };

  const handleReject = () => {
    Swal.fire({
      title: 'Reject this registration?',
      input: 'textarea',
      inputLabel: 'Reason (optional)',
      inputPlaceholder: 'Type a reason...',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, reject',
    }).then((result) => {
      if (!result.isConfirmed) return;
      fetch(`${API_BASE_URL}/users/update_registration_status.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: employeeId, newStatus: 'rejected', reason: result.value }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            Swal.fire('Rejected', 'The registration has been rejected.', 'success').then(() => navigate('/users'));
          } else {
            Swal.fire('Error', data.message || 'Failed to reject.', 'error');
          }
        })
        .catch(() => Swal.fire('Error', 'An error occurred while rejecting.', 'error'));
    });
  };

  const handleDelete = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This hides the employee from all lists and prevents login, but preserves their training history.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
    }).then((result) => {
      if (!result.isConfirmed) return;
      fetch(`${API_BASE_URL}/users/delete_user.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: employeeId }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            Swal.fire('Deleted', 'The employee has been deleted.', 'success').then(() => navigate('/users'));
          } else {
            Swal.fire('Error', data.message || 'Failed to delete.', 'error');
          }
        })
        .catch(() => Swal.fire('Error', 'An error occurred while deleting.', 'error'));
    });
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  // Pending: still editable, same message tone as register.php's own
  // response. Rejected: read-only, with the admin's reason if one was given
  // (update_registration_status.php's reason field is optional).
  const statusBanner = isRestrictedOwnProfile && (
    <div className={`alert ${employee.status === 'rejected' ? 'alert-danger' : 'alert-warning'}`}>
      {employee.status === 'pending' && 'Your registration is pending approval. You can update your contact details below in the meantime.'}
      {employee.status === 'rejected' && (
        <>Your registration was not approved.{employee.reject_reason ? ` Reason: ${employee.reject_reason}` : ''}</>
      )}
    </div>
  );

  const profileFormContent = (
          <Form>
            <Form.Group controlId="photo" className="mb-3">
              <Form.Label className="d-block">Photo</Form.Label>
              <img
                src={employee.avatar ? `${BASE_URL}/uploads/${employee.avatar}` : `${BASE_URL}/images/avatar.png`}
                alt="Profile"
                width="200"
                height="200"
                style={{ objectFit: 'cover' }}
                className="rounded mb-2 d-block"
              />
              {(canEditOwnProfile || canManage) && (
                <>
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handlePhotoChange}
                    disabled={photoUploading}
                  />
                  {photoUploading && <Form.Text>Uploading...</Form.Text>}
                </>
              )}
            </Form.Group>
            <Form.Group controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={employee.name} readOnly />
            </Form.Group>
            <Form.Group controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={employee.email}
                onChange={canEditOwnProfile ? handleOwnFieldChange : undefined}
                readOnly={!canEditOwnProfile}
              />
            </Form.Group>
            <Form.Group controlId="nationality">
              <Form.Label>Nationality</Form.Label>
              <Form.Control
                as="select"
                name="nationality"
                value={employee.nationality || ''}
                onChange={canEditOwnProfile ? handleOwnFieldChange : undefined}
                disabled={!canEditOwnProfile}
              >
                <option value="">Select a nationality</option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="ic_no">
              <Form.Label>IC/Passport No.</Form.Label>
              <Form.Control type="text" value={employee.ic_no} readOnly />
            </Form.Group>
            <Form.Group controlId="role">
              <Form.Label>Role</Form.Label>
              <Form.Control type="text" value={employee.role} readOnly />
            </Form.Group>
            <Form.Group controlId="company">
              <Form.Label>Company/Subsidiary</Form.Label>
              <Form.Control type="text" value={employee.company} readOnly />
            </Form.Group>
            <Form.Group controlId="department">
              <Form.Label>Department</Form.Label>
              <Form.Control type="text" value={employee.department} readOnly />
            </Form.Group>
            <Form.Group controlId="designation">
              <Form.Label>Designation</Form.Label>
              <Form.Control type="text" value={employee.designation} readOnly />
            </Form.Group>
            <Form.Group controlId="employeeNo">
              <Form.Label>Employee No.</Form.Label>
              <Form.Control type="text" value={employee.employee_no} readOnly />
            </Form.Group>
            <Form.Group controlId="contactNo">
              <Form.Label>Contact No.</Form.Label>
              <Form.Control
                type="text"
                name="contact_no"
                value={employee.contact_no}
                onChange={canEditOwnProfile ? handleOwnFieldChange : undefined}
                readOnly={!canEditOwnProfile}
              />
            </Form.Group>
            <Form.Group controlId="emergencyContactNo">
              <Form.Label>Emergency Contact No.</Form.Label>
              <Form.Control
                type="text"
                name="emergency_contact_no"
                value={employee.emergency_contact_no}
                onChange={canEditOwnProfile ? handleOwnFieldChange : undefined}
                readOnly={!canEditOwnProfile}
              />
            </Form.Group>
            <Form.Group controlId="relationship">
              <Form.Label>Relationship</Form.Label>
              <Form.Control
                type="text"
                name="relationship"
                value={employee.relationship}
                onChange={canEditOwnProfile ? handleOwnFieldChange : undefined}
                readOnly={!canEditOwnProfile}
              />
            </Form.Group>
            {canEditOwnProfile && (
              <Button variant="primary" className="mt-2" onClick={handleSaveOwnProfile}>
                Save Profile
              </Button>
            )}
            {canManage && (
              <div className="mt-2">
                {employee.status === 'pending' ? (
                  <>
                    <Button variant="success" className="me-2" onClick={handleApprove}>Approve</Button>
                    <Button variant="warning" onClick={handleReject}>Reject</Button>
                  </>
                ) : (
                  <>
                    <Link to={`/users/edit/${employeeId}`}>
                      <Button variant="primary" className="me-2">Edit</Button>
                    </Link>
                    <Button variant="danger" onClick={handleDelete}>Delete</Button>
                  </>
                )}
              </div>
            )}
          </Form>
  );

  if (isRestrictedOwnProfile) {
    return (
      <div className='p-2'>
        <h1>User Details</h1>
        {statusBanner}
        {profileFormContent}
      </div>
    );
  }

  return (
    <div className='p-2'>
      <h1>User Details</h1>
      <Tabs activeKey={selectedTab} onSelect={(tab) => setSelectedTab(tab)} id="employee-details-tabs">
        <Tab eventKey="profile" title="Profile">
          {profileFormContent}
        </Tab>
        {/* Goals tab temporarily hidden — unused stub, no defined scope yet
            (see NOTES.md P3 "Personal Goal Achievement"). Re-enable once
            real requirements/content exist. */}
        <Tab eventKey="qualification" title="Qualification/Role">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Certificate/Reference No.</th>
                <th>Award Date</th>
                <th>Expiry Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {employee.qualifications && employee.qualifications.length > 0 ? (
                employee.qualifications.map(q => (
                  <tr key={q.id}>
                    <td>{q.name}</td>
                    <td>{q.type}</td>
                    <td>{q.certificate_no || '-'}</td>
                    <td>{q.awarded_date || '-'}</td>
                    <td>{q.expiry_date || '-'}</td>
                    <td>{qualificationExpiryBadge(q.expiry_date)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">No qualifications/roles recorded</td>
                </tr>
              )}
            </tbody>
          </Table>
          {(isOwnProfile || canManage) && (
            <Form className="mt-3">
              <h5>Add Qualification/Role</h5>
              <Form.Group controlId="newQualificationId">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  as="select"
                  name="qualificationId"
                  value={newQualification.qualificationId}
                  onChange={handleNewQualificationChange}
                >
                  <option value="">Select a qualification/role</option>
                  {allQualifications.map(q => (
                    <option key={q.id} value={q.id}>{q.name}</option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group controlId="newQualificationType" className="mt-2">
                <Form.Label>Type</Form.Label>
                <Form.Control
                  type="text"
                  value={allQualifications.find(q => String(q.id) === String(newQualification.qualificationId))?.type || ''}
                  readOnly
                />
              </Form.Group>
              <Form.Group controlId="newQualificationCertificateNo" className="mt-2">
                <Form.Label>Certificate/Reference Number</Form.Label>
                <Form.Control
                  type="text"
                  name="certificateNo"
                  value={newQualification.certificateNo}
                  onChange={handleNewQualificationChange}
                />
              </Form.Group>
              <Form.Group controlId="newQualificationAwardedDate" className="mt-2">
                <Form.Label>Award Date</Form.Label>
                <Form.Control
                  type="date"
                  name="awardedDate"
                  value={newQualification.awardedDate}
                  onChange={handleNewQualificationChange}
                />
              </Form.Group>
              <Form.Group controlId="newQualificationExpiryDate" className="mt-2">
                <Form.Label>Expiry Date</Form.Label>
                <Form.Control
                  type="date"
                  name="expiryDate"
                  value={newQualification.expiryDate}
                  onChange={handleNewQualificationChange}
                />
              </Form.Group>
              <Button variant="primary" className="mt-2" onClick={handleAddQualification}>Add</Button>
            </Form>
          )}
        </Tab>
        <Tab eventKey="attendedCourses" title="Course Attended">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Course Code</th>
                <th>Course Title</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Certificate</th>
              </tr>
            </thead>
            <tbody>
            {employee.attendedCourses && employee.attendedCourses.length > 0 ? (
                employee.attendedCourses
                  .slice((attendedCoursesPage - 1) * PAGE_SIZE, attendedCoursesPage * PAGE_SIZE)
                  .map(course => (
                  <tr key={`${course.code}-${course.start_date}`}>
                    <td>{course.code}</td>
                    <td>{course.title}</td>
                    <td>{course.start_date}</td>
                    <td>{course.end_date}</td>
                    <td>
                      {course.certificate_id && (
                        <a
                          href={`${API_BASE_URL}/certificates/download_certificate.php?id=${course.certificate_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary btn-sm me-1"
                        >
                          Certificate
                        </a>
                      )}
                      {course.competency_certificate_id && (
                        <a
                          href={`${API_BASE_URL}/certificates/download_certificate.php?id=${course.competency_certificate_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-warning btn-sm"
                        >
                          Competency Certificate
                        </a>
                      )}
                      {!course.certificate_id && !course.competency_certificate_id && '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5">No courses attended</td>
                </tr>
              )}
            </tbody>
          </Table>
          <PaginationControls
            page={attendedCoursesPage}
            totalPages={Math.max(1, Math.ceil((employee.attendedCourses?.length || 0) / PAGE_SIZE))}
            onPageChange={setAttendedCoursesPage}
          />
        </Tab>
        <Tab eventKey="keyInstructor" title="Key Instructor Eligibility">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Course Code</th>
                <th>Course Title</th>
                <th>Date Gained</th>
              </tr>
            </thead>
            <tbody>
            {employee.keyInstructorEligibleCourses && employee.keyInstructorEligibleCourses.length > 0 ? (
                employee.keyInstructorEligibleCourses
                  .slice((keyInstructorPage - 1) * PAGE_SIZE, keyInstructorPage * PAGE_SIZE)
                  .map(course => (
                  <tr key={course.code}>
                    <td>{course.code}</td>
                    <td>{course.title}</td>
                    <td>{course.issued_at ? course.issued_at.split(' ')[0] : ''}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3">Not yet eligible for any course</td>
                </tr>
              )}
            </tbody>
          </Table>
          <PaginationControls
            page={keyInstructorPage}
            totalPages={Math.max(1, Math.ceil((employee.keyInstructorEligibleCourses?.length || 0) / PAGE_SIZE))}
            onPageChange={setKeyInstructorPage}
          />
        </Tab>
        {(isOwnProfile || canManage) && (
        <Tab eventKey="resetPassword" title="Reset Password">
          <Form onSubmit={handleChangePassword}>
            {isOwnProfile && (
              <Form.Group controlId="currentPassword">
                <Form.Label>Current Password</Form.Label>
                <Form.Control
                  type="password"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordFormChange}
                  required
                />
              </Form.Group>
            )}
            <Form.Group controlId="newPassword" className="mt-2">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordFormChange}
                minLength={8}
                required
              />
            </Form.Group>
            <Form.Group controlId="confirmPassword" className="mt-2">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordFormChange}
                minLength={8}
                required
              />
            </Form.Group>
            <Button variant="primary" type="submit" className="mt-2">Reset Password</Button>
          </Form>
        </Tab>
        )}
        <Tab eventKey="attachment" title="Attachment">
          <AttachmentsTab 
            categoryId={employeeId} 
            category="employee_documents" 
            currentUserId={loggedInUser.id} // Pass current user ID
          />
        </Tab>
      </Tabs>
    </div>
  );
}

export default EmployeeDetails;
